import { FaMapMarkerAlt, FaGraduationCap } from "react-icons/fa";
import {
  SiTypescript,
  SiReact,
  SiNodedotjs,
  SiSupabase,
  SiRedis,
  SiShopify,
  SiCapacitor,
  SiRedux,
  SiOpenai,
  SiGithub,
  SiPython,
  SiPostgresql,
} from "react-icons/si";
import { LuBrainCircuit, LuWorkflow } from "react-icons/lu";
import { TbApi } from "react-icons/tb";

const stackIcons = {
  TypeScript: SiTypescript,
  "React / Next.js": SiReact,
  React: SiReact,
  "Node.js": SiNodedotjs,
  Supabase: SiSupabase,
  "Supabase (pgvector)": SiSupabase,
  Redis: SiRedis,
  "Shopify GraphQL API": SiShopify,
  Capacitor: SiCapacitor,
  "Plaid API": TbApi,
  "Redux Toolkit (RTK)": SiRedux,
  "OpenAI SDK": SiOpenai,
  "GitHub API": SiGithub,
  n8n: LuWorkflow,
  LLMs: LuBrainCircuit,
  "LLMs / RAG": LuBrainCircuit,
  Python: SiPython,
  PostgreSQL: SiPostgresql,
  "ClickUp / JIRA": TbApi,
  Greptile: LuBrainCircuit,
};

const experiences = [
  {
    company: "Bloom Nutrition",
    location: "Remote",
    role: "Software Engineer (Contract) · Influencer-Marketing Growth Systems",
    period: "2024 – Present",
    projects: [
      {
        title: "Influencer Discovery & Outreach Automation",
        bullets: [
          "Built an automated influencer-discovery pipeline in n8n that scouts creators on TikTok and Instagram against defined fit criteria, replacing manual sourcing and multiplying outreach-team throughput.",
          "Designed and wrote the multi-factor creator-ranking algorithm at the core of the pipeline — a weighted scoring function grading each scouted creator across three pillars (engagement, on-topic relevance, and posting cadence) into a single ranked score, so outreach prioritizes the highest-value prospects first.",
          "Hardened the ranking model against gaming and noise: median-based aggregation over means, winsorization at the 95th percentile, an effective-follower floor to prevent micro-account inflation, and two independent disqualifier gates (low-views precheck and floor-consistency check) that can reject a creator regardless of raw score.",
          "Re-architected the discovery system from the n8n workflow into a native TypeScript service, adding request batching and parallelization to query social-platform data at substantially higher speed and volume.",
          "Integrated the OpenAI SDK to score and enrich discovered creators before upsert, backed by a Supabase data layer with a Redis-backed job queue and idempotency to keep the pipeline fast and safe to re-run.",
        ],
        stack: [
          "TypeScript",
          "n8n",
          "Redis",
          "Supabase",
          "OpenAI SDK",
        ],
      },
      {
        title: "Scouter & Outreach UI",
        bullets: [
          "Built the scouter-facing UI the outreach team uses to work discovered creators — surfacing each creator's ranking score and profile data, with an approve/reject review workflow that promotes qualified creators out of the discovery queue.",
          "Added outreach tooling to the same interface so the team can email creators directly and track who has been contacted, keeping sourcing, ranking, and outreach in one place.",
        ],
        stack: ["TypeScript", "React / Next.js", "Supabase"],
      },
      {
        title: "AI Ticket-Generation Tool — Codebase-Aware Engineering Workflow",
        bullets: [
          "Built a full-stack internal tool (Next.js front end → n8n orchestration → LLM) that turns a feature objective plus codebase context into structured, ready-to-work ClickUp / JIRA tickets, replacing manual ticket writing.",
          "Implemented a RAG pipeline over the codebase with Supabase pgvector — indexing GitHub branches, semantically chunking source files, and retrieving the most relevant code so generated tickets reference real implementation detail; integrated Greptile for deeper codebase-aware querying.",
          "Added WebSocket-based real-time notifications to surface ticket-generation status to users as jobs complete.",
        ],
        stack: [
          "TypeScript",
          "React / Next.js",
          "Node.js",
          "n8n",
          "Redis",
          "Supabase (pgvector)",
          "OpenAI SDK",
          "GitHub API",
          "ClickUp / JIRA",
          "Greptile",
        ],
      },
    ],
  },
  {
    company: "Felipa",
    location: "Remote",
    role: "Software Engineer (Contract) · AI Personal-Finance App",
    period: "2023 – 2024",
    projects: [
      {
        bullets: [
          "Built the reporting UI for a consumer personal-finance app that uses an LLM to summarize users' bank activity, integrating Plaid to securely connect accounts and feed transaction data into the summarization layer.",
          "Made the app multi-platform from a single codebase — packaging the web app for iOS and Android with Capacitor.",
        ],
        stack: ["TypeScript", "React", "Capacitor", "Plaid API", "LLMs"],
      },
    ],
  },
  {
    company: "Reina — Software Consulting Agency",
    location: "Remote",
    role: "Software Engineer (Contract)",
    period: "2022 – 2023",
    note: "Contract engineering delivered through Reina, a software consulting agency that places engineers on client engagements (Upwork-style). Client names withheld under confidentiality.",
    projects: [
      {
        title: "College Transfer-Credit Evaluation Platform (confidential client)",
        bullets: [
          "Delivered full-stack features across a college transfer-credit platform, building student-facing flows to navigate and request course and program transfers across institutions and roughly 1,000 academic programs.",
          "Built an AI-powered transcript-ingestion feature that extracts and parses uploaded transcript PDFs and maps courses into the platform database, removing a manual data-entry step from the upload process.",
          "Engineered a course-equivalency matching engine and a credit-allocation algorithm over a bipartite-matching course allocator, computing how a student's completed courses apply toward target-program requirements.",
          "Implemented an articulation-pathway awards layer on top of the allocator, restructuring it into a pathway-first model that prevents credit double-counting and caps awards at each requirement's ceiling, plus a \"what-if\" audit simulator for prospective transfer scenarios.",
        ],
        stack: [
          "TypeScript",
          "React / Next.js",
          "Node.js",
          "Python",
          "PostgreSQL",
          "LLMs",
        ],
      },
      {
        title: "ryventory — Inventory & Multi-Marketplace Crosslisting Platform",
        bullets: [
          "Designed and built the application end to end — the full Next.js / React UI and component library on a custom design system, Supabase Auth, and a normalized Product → Item → Listing data model.",
          "Built an event-driven crosslisting engine that syncs inventory across eBay, Shopify, and other marketplaces and auto-delists sold items, using webhook subscriptions and a Redis-backed job queue with idempotency to prevent overselling.",
          "Integrated the Shopify GraphQL Admin API to run the storefront as POS and source of truth, and built a photo-based pricing tool that identifies items with a multimodal LLM and pulls market comps from PriceCharting and eBay sold listings.",
          "Built a catalog entity-resolution engine that resolves incoming items (barcode misses, LLM identification, manual entry) to canonical Product records using token-based fuzzy matching with blocking to skip O(n²) comparisons, auto-merging clear matches and routing the rest to review.",
        ],
        stack: [
          "TypeScript",
          "React / Next.js",
          "Node.js",
          "Supabase",
          "Redis",
          "Shopify GraphQL API",
        ],
      },
    ],
  },
];

const education = [
  {
    school: "Miami Dade College",
    location: "Miami, FL",
    credential:
      "B.S. in Information Systems Technology — Software Engineering Concentration",
  },
  {
    school: "Thinkful",
    location: "Remote",
    credential: "Certificate, Software Engineering",
  },
];

function StackChip({ label }) {
  const Icon = stackIcons[label];
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 text-tag font-semibold rounded-full bg-surface text-muted">
      {Icon && <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
      {label}
    </span>
  );
}

function ExperienceCard({ exp }) {
  return (
    <article className="w-full p-6 sm:p-8 bg-bg border border-border rounded-3xl shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <h3 className="text-h4 text-heading">{exp.company}</h3>
        <span className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-muted">
          <FaMapMarkerAlt className="w-3.5 h-3.5 text-accent" />
          {exp.location}
        </span>
      </div>
      <div className="flex flex-col gap-0.5 mt-1 sm:flex-row sm:items-baseline sm:justify-between">
        <p className="font-semibold text-text">{exp.role}</p>
        <p className="text-body-sm font-semibold text-accent">{exp.period}</p>
      </div>
      {exp.note && <p className="mt-1 text-body-sm text-muted">{exp.note}</p>}

      <div className="flex flex-col gap-6 mt-5">
        {exp.projects.map((project, pIdx) => (
          <div key={pIdx}>
            {project.title && (
              <p className="mb-3 font-semibold text-heading">{project.title}</p>
            )}
            <ul className="flex flex-col gap-3">
              {project.bullets.map((bullet, bIdx) => (
                <li key={bIdx} className="flex gap-3 text-body text-text">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2 mt-4">
              {project.stack.map((tech) => (
                <StackChip key={tech} label={tech} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </article>
  );
}

function EducationCard({ item }) {
  return (
    <article className="flex flex-col w-full p-6 bg-bg border border-border rounded-3xl shadow-card transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover">
      <div className="flex items-center gap-3">
        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-surface text-accent shrink-0">
          <FaGraduationCap className="w-5 h-5" />
        </span>
        <div className="flex flex-col">
          <h4 className="font-semibold text-heading">{item.school}</h4>
          <span className="text-body-sm text-muted">{item.location}</span>
        </div>
      </div>
      <p className="mt-4 text-body-sm text-text">{item.credential}</p>
      {item.period && (
        <p className="mt-2 text-body-sm font-semibold text-accent">
          {item.period}
        </p>
      )}
    </article>
  );
}

function ResumeViewer() {
  return (
    <div className="w-full px-6 md:px-8 lg:px-16 mt-10">
      <div className="flex flex-col gap-6">
        {experiences.map((exp) => (
          <ExperienceCard key={exp.company} exp={exp} />
        ))}
      </div>

      <p className="eyebrow text-eyebrow mt-14">Education</p>
      <div className="grid grid-cols-1 gap-6 mt-4 sm:grid-cols-2">
        {education.map((item) => (
          <EducationCard key={item.school} item={item} />
        ))}
      </div>
    </div>
  );
}

export default ResumeViewer;
